import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface SidebarContextType {
  isSidebarVisible: boolean;
  isCollapsed: boolean;
  toggleSidebar: () => void;
  showSidebar: () => void;
  hideSidebar: () => void;
  setCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Auto-hide sidebar on small screens initially, but don't toggle on resize
  // This allows users to keep the sidebar open if they want
  useEffect(() => {
    const handleInitialSize = () => {
      const isMobile = window.innerWidth < 768;
      // Only set initial state based on screen size
      if (isMobile) {
        setIsSidebarVisible(false);
      } else {
        setIsSidebarVisible(true);
      }
    };

    // Set initial state only once
    handleInitialSize();

    // Update popup behavior when window is resized
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarVisible(prev => !prev);
  };

  const showSidebar = () => {
    setIsSidebarVisible(true);
  };

  const hideSidebar = () => {
    setIsSidebarVisible(false);
  };

  return (
    <SidebarContext.Provider value={{ 
      isSidebarVisible, 
      isCollapsed, 
      toggleSidebar, 
      showSidebar, 
      hideSidebar,
      setCollapsed: setIsCollapsed,
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
